<template>
  <div class="flex mb-12 bulk-collection-form">
    <div class="content-left-panel">
      <div class="flex flex-wrap mt-10">
        <upload-file
          automatic
          text="Bulk Collection creation and update by CSV"
          fileName="source"
          :uploadMethod="uploadCSV"
          @on-error="errorUpload"
        />
        <small class="ml-3">
          Choose a file, this will be uploaded automatically
        </small>
      </div>
      <div id="validation-results" class="bulk-results">
        <h4 class="mt-10 mb-5">Results</h4>
        <template v-if="resultsType === 'validation'">
          <results-tree
            id="validation-errors"
            v-if="currentCsv.validation_results.errors"
            :results="currentCsv.validation_results.errors"
            type="Error"
            color="danger"
            showDetails
          />
          <results-tree
            id="validation-warnings"
            v-if="currentCsv.validation_results.warnings"
            :results="currentCsv.validation_results.warnings"
            type="Warning"
            color="warning"
            showDetails
          />
          <results-tree
            id="validation-success"
            v-if="currentCsv.validation_results.success"
            :results="currentCsv.validation_results.success"
            type="Success"
            color="success"
          />
          <div
            v-if="currentCsv.status === 'validated'"
            class="buttons-container flex justify-end gap-5 mt-5"
          >
            <vs-button
              icon-pack="feather"
              icon="icon-refresh-cw"
              type="border"
              @click="validateCSV(currentCsv.id)"
            >
              Validate
            </vs-button>
            <vs-button
              v-if="!currentCsv.validation_results.errors.lines.length"
              icon-pack="feather"
              icon="icon-save"
              color="primary"
              icon-after
              @click="importCSV(currentCsv.id)"
            >
              Import
            </vs-button>
          </div>
        </template>
        <template v-if="resultsType === 'import'">
          <results-tree
            id="import-errors"
            v-if="currentCsv.import_results.errors"
            :results="currentCsv.import_results.errors"
            type="Error"
            color="danger"
            showDetails
          />
          <results-tree
            id="import-updations"
            v-if="currentCsv.import_results.updations"
            :results="currentCsv.import_results.updations"
            type="Update"
            color="warning"
          />
          <results-tree
            id="import-creations"
            v-if="currentCsv.import_results.creations"
            :results="currentCsv.import_results.creations"
            type="Create"
            color="success"
          />
        </template>
      </div>
      <vs-divider />
      <vs-table-m
        :total="collectionsCsv.length"
        :data="collectionsCsv"
        class="mt-10"
        hoverFlat
        stripe
      >
        <template slot="header">
          <div class="flex items-center w-full">
            <h4 class="m-2 my-4">List of collections csv</h4>
            <div class="ml-auto mr-6 text-lg font-semibold">
              {{ collectionsCsv.length }} results
            </div>
          </div>
        </template>
        <template slot="thead">
          <vs-th sort-key="id"> ID </vs-th>
          <vs-th sort-key="status"> Status </vs-th>
          <vs-th sort-key="author"> Author </vs-th>
          <vs-th sort-key="created"> Created </vs-th>
          <vs-th sort-key="no-sort"> Actions </vs-th>
        </template>
        <template slot-scope="{ data }">
          <vs-tr
            v-for="csvInstance in data"
            :data="csvInstance"
            :key="csvInstance.id"
          >
            <vs-td>
              {{ csvInstance.id }}
            </vs-td>
            <vs-td>
              {{ csvInstance.status }}
            </vs-td>
            <vs-td>
              {{ csvInstance.author }}
            </vs-td>
            <vs-td>
              {{
                formatDistance(new Date(csvInstance.created), new Date(), {
                  addSuffix: true,
                })
              }}
            </vs-td>
            <vs-td>
              <div class="flex gap-5" v-if="csvInstance.status === 'validated'">
                <feather-icon
                  icon="RefreshCwIcon"
                  svgClasses="h-5 w-5 text-blue"
                  @click="validateCSV(csvInstance.id)"
                />
                <feather-icon
                  icon="SaveIcon"
                  svgClasses="h-5 w-5 text-blue"
                  @click="importCSV(csvInstance.id)"
                />
              </div>
            </vs-td>
          </vs-tr>
        </template>
      </vs-table-m>
      <div v-if="collectionsCsv.length" class="mt-5">
        <p>
          <strong>initial:</strong> There was a problem with the request and it
          wasn't properly handled
        </p>
        <p>
          <strong>validated:</strong> The file was validated and it's ready to
          be imported, click the "save" icon to proceed
        </p>
        <p>
          <strong>imported:</strong> The file was imported successfully to the
          collection library
        </p>
        <p>
          <strong>cancelled:</strong> The upload was cancelled due an error.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import UploadFile from '../components/UploadFile'
import ResultsTree from '@/components/ResultsTree'
import VsTableM from '@/components/VsTableM'
import { formatDistance } from 'date-fns'

export default {
  components: {
    UploadFile,
    ResultsTree,
    'vs-table-m': VsTableM,
  },
  data: () => {
    return {
      currentCsv: null,
      resultsType: null,
      formatDistance: formatDistance,
    }
  },
  created() {
    this.getUploads()
  },
  computed: {
    ...mapGetters('collections', ['collectionsCsv']),
  },
  methods: {
    ...mapActions('collections', [
      'getUploadedCollectionsCsvAction',
      'uploadCollectionCsvAction',
      'importCollectionCsvAction',
      'validateCollectionCsvAction',
    ]),
    errorUpload(error) {
      this.showError(error)
    },
    async getUploads() {
      try {
        this.$vs.loading()
        await this.getUploadedCollectionsCsvAction()
      } catch (error) {
        this.showError(error)
      } finally {
        this.$vs.loading.close()
      }
    },
    setCurrentCsvResults(results, type) {
      this.resultsType = type
      this.currentCsv = results
      this.scrollToResults()
    },
    async uploadCSV(formData) {
      try {
        this.$vs.loading()
        const response = await this.uploadCollectionCsvAction(formData)
        await this.getUploadedCollectionsCsvAction()
        this.setCurrentCsvResults(response, 'validation')
        this.$vs.notify({
          color: 'success',
          title: 'Upload Success',
          text: 'The file was uploaded successfully',
        })
      } catch (error) {
        this.showError(error)
      } finally {
        this.$vs.loading.close()
      }
    },
    async validateCSV(csvId) {
      try {
        this.$vs.loading()
        const response = await this.validateCollectionCsvAction(csvId)
        await this.getUploadedCollectionsCsvAction()
        this.setCurrentCsvResults(response, 'validation')
        this.$vs.notify({
          color: 'success',
          title: 'Validation Success',
          text: 'The file was validated successfully',
        })
      } catch (error) {
        this.showError(error)
      } finally {
        this.$vs.loading.close()
      }
    },
    async importCSV(csvId) {
      try {
        this.$vs.loading()
        const response = await this.importCollectionCsvAction(csvId)
        await this.getUploadedCollectionsCsvAction()
        this.setCurrentCsvResults(response, 'import')
        this.$vs.notify({
          color: 'success',
          title: 'Import Success',
          text: 'The file was imported successfully',
        })
      } catch (error) {
        this.showError(error)
      } finally {
        this.$vs.loading.close()
      }
    },
    scrollToResults() {
      const e = document.getElementById('validation-results')
      e.scrollIntoView({ behavior: 'smooth' })
    },
  },
}
</script>
<style lang="scss">
.bulk-collection-form {
  .content-left-panel {
    .con-img-upload {
      .img-upload {
        height: 100px;
        width: 100px;
      }
      .text-archive {
        font-size: 0.8em;
      }
    }
    .con-input-upload {
      margin: 0;
      width: 100%;
      height: 100px;
    }
  }
}
</style>
